import {
  ForwardedRef,
  MouseEventHandler,
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import sprite from '../../../assets/icons/sprite.svg';

import classNames from 'classnames';
import classes from './IconLabel.module.scss';

export enum IconLabelSizes {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

export enum IconLabelHintDirection {
  top = 'top',
  right = 'right', //default
  bottom = 'bottom',
  bottomLeft = 'bottom-left',
  left = 'left',
}

interface IconLabelProps {
  icon?: JSX.Element; // icon element
  iconUrl?: string; // icon href
  iconId?: string; // icon id from svg sprite
  iconSize?: number;
  label?: string;
  ariaLabel?: string;
  labelSize?: IconLabelSizes;
  color?: string;
  hoverColor?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  onMouseDown?: MouseEventHandler<HTMLElement>;
  className?: string;
  labelClassName?: string;
  labelFirst?: boolean;
  singleColor?: boolean;
  nonClickable?: boolean;
  disabled?: boolean;
  withPadding?: boolean;
  backgroundColor?: string;
  data?: string;
  hintDirection?: IconLabelHintDirection;
}

const DEFAULT_COLORS = {
  main: '#43445B',
  hover: '#707070',
  disabled: '#B3B3B3',
};

const BASE_FONT_SIZE_PX = 16;

export const IconLabel = memo(
  forwardRef<HTMLElement, IconLabelProps>(
    (
      {
        icon,
        iconUrl,
        iconId,
        iconSize = 24,
        label,
        ariaLabel,
        labelSize = IconLabelSizes.medium,
        color,
        hoverColor,
        onClick,
        onMouseDown,
        className,
        labelClassName,
        labelFirst,
        singleColor,
        nonClickable,
        disabled,
        withPadding,
        backgroundColor,
        data,
        hintDirection,
      },
      ref
    ) => {
      const iconSizeRem = `${iconSize / BASE_FONT_SIZE_PX}rem`;

      const textColor = useMemo(
        () => (disabled ? DEFAULT_COLORS.disabled : color ?? DEFAULT_COLORS.main),
        [color, disabled]
      );

      const textHoverColor = useMemo(() => {
        if (singleColor || disabled) {
          return textColor;
        }
        return hoverColor ?? DEFAULT_COLORS.hover;
      }, [disabled, hoverColor, singleColor, textColor]);

      const iconUrlElementRef = useRef<HTMLSpanElement>(null);

      const renderLabel = useMemo(() => {
        if (!label) {
          return null;
        }
        return (
          <span
            className={classNames(
              classes['icon-label__label'],
              classes[`icon-label__label--${labelSize}`],
              labelClassName
            )}
          >
            {label}
          </span>
        );
      }, [label, labelClassName, labelSize]);

      const renderIconUrlElement = useMemo(() => {
        if (!iconUrl) {
          return null;
        }
        return (
          <span
            ref={iconUrlElementRef}
            style={{
              width: iconSizeRem,
              height: iconSizeRem,
              backgroundColor: textColor,
              flexShrink: 0,
              mask: `url('${iconUrl}') no-repeat center`,
              WebkitMask: `url('${iconUrl}') no-repeat center`,
            }}
          ></span>
        );
      }, [iconSizeRem, iconUrl, textColor]);

      const changeColor = useCallback((currentTarget: EventTarget & HTMLElement, color: string) => {
        currentTarget.style.color = color;

        if (iconUrlElementRef?.current) {
          iconUrlElementRef.current.style.backgroundColor = color;
        }
      }, []);

      if ([icon, iconUrl, iconId, label].every((value) => !Boolean(value))) {
        return null;
      }

      const Element = onClick && !nonClickable ? 'button' : 'div';

      return (
        <Element
          ref={ref as ForwardedRef<HTMLButtonElement & HTMLDivElement>}
          className={classNames(className, classes['icon-label'], {
            [classes['icon-label--non-clickable']]: nonClickable,
            [classes['icon-label--disabled']]: disabled,
            [classes['icon-label--with-padding']]: withPadding,
            [classes['icon-label--data']]: data,
            [classes[`icon-label--data-hint-${hintDirection}`]]: data && hintDirection,
          })}
          style={{ color: textColor, backgroundColor }}
          onMouseLeave={({ currentTarget }) => changeColor(currentTarget, textColor)}
          onMouseOver={({ currentTarget }) => changeColor(currentTarget, textHoverColor)}
          onClick={onClick}
          onMouseDown={onMouseDown}
          {...{ data }}
          {...(Element === 'button' && {
            type: 'button',
            disabled,
            ...(!label && { 'aria-label': ariaLabel ?? data }),
          })}
        >
          {labelFirst && renderLabel}
          {icon}
          {renderIconUrlElement}
          {iconId && (
            <svg
              style={{ width: iconSizeRem, height: iconSizeRem }}
              className={classes['icon-label__icon']}
            >
              <use href={`${sprite}#${iconId}`} />
            </svg>
          )}
          {!labelFirst && renderLabel}
        </Element>
      );
    }
  )
);
